/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MINE_PAGE_FILTERS_SIDEBAR
 * Date:         2026-01-29
 * Modified By:  Cursor
 *
 * Description:
 * Add filters sidebar to /broadcast/mine-all; same pattern as BroadcastsTabs.
 * Pipeline: flatten → filter → paginate → reconstruct.
 */
/**
 * Phase 7: My Broadcasts page at /broadcast/mine-all.
 * Fetches mine, shows table; empty → redirect /broadcast/new. SuccessMessage on create redirect.
 */
// [2026-02-10] Mobile: Added useState for filter drawer state
import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// [2026-02-10] Mobile: Responsive detection and filter drawer
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import { FilterDrawer } from "../../../../components/ui/FilterDrawer";
import SuccessMessage from "../../../../components/SuccessMessage/SuccessMessage";
import BroadcastsHeader from "../broadcasts-list/components/BroadcastsHeader";
import DashboardBroadcastSummaryTable from "../../../dashboard/DashboardBroadcastSummaryTable";
import { PaginationControls } from "../../../../components/ui/PaginationControls";
import { BroadcastFilters } from "../../../../components/broadcast/BroadcastFilters";
import { useBroadcastsMine } from "./useBroadcastsMine";
import {
  flattenBroadcasts,
  filterFlattenedBroadcasts,
  paginateBroadcasts,
  calculateTotalPages,
} from "../broadcasts-list/utils/broadcastHelpers";
import styles from "../broadcasts-list/BroadcastsListPage.module.scss";

const BroadcastMinePage: React.FC = () => {
  const navigate = useNavigate();
  // [2026-02-10] Mobile: Responsive detection and filter drawer state
  const { isMobile } = useMediaQuery();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const {
    data,
    loading,
    error,
    refresh,
    filtersCollapsed,
    toggleFilters,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleItemsPerPageChange,
    broadcastFilterKeyword,
    broadcastSKU,
    broadcastType,
    broadcastManufacturer,
    broadcastCategory,
    broadcastQty,
    broadcastCondition,
    broadcastCountry,
    broadcastCompany,
    handleFilterChange,
    handleClearFilters,
  } = useBroadcastsMine();

  const allFlattenedBroadcasts = useMemo(() => flattenBroadcasts(data), [data]);
  const filteredBroadcasts = useMemo(
    () =>
      filterFlattenedBroadcasts(allFlattenedBroadcasts, {
        keyword: broadcastFilterKeyword,
        sku: broadcastSKU,
        type: broadcastType,
        manufacturer: broadcastManufacturer,
        category: broadcastCategory,
        qty: broadcastQty,
        condition: broadcastCondition,
        country: broadcastCountry,
        company: broadcastCompany,
      }),
    [
      allFlattenedBroadcasts,
      broadcastFilterKeyword,
      broadcastSKU,
      broadcastType,
      broadcastManufacturer,
      broadcastCategory,
      broadcastQty,
      broadcastCondition,
      broadcastCountry,
      broadcastCompany,
    ]
  );
  const paginatedBroadcasts = useMemo(
    () => paginateBroadcasts(filteredBroadcasts, currentPage, itemsPerPage),
    [filteredBroadcasts, currentPage, itemsPerPage]
  );
  const totalPages = useMemo(
    () => calculateTotalPages(filteredBroadcasts.length, itemsPerPage),
    [filteredBroadcasts.length, itemsPerPage]
  );
  const reconstructed = useMemo(
    () =>
      paginatedBroadcasts.map((item) => ({
        ...item.broadcastGroup,
        items: [item],
      })) as any[],
    [paginatedBroadcasts]
  );

  // [2026-02-10] Mobile: Count active filters for badge
  const activeFilterCount = [
    broadcastFilterKeyword,
    broadcastSKU,
    broadcastType,
    broadcastManufacturer,
    broadcastCategory,
    broadcastQty,
    broadcastCondition,
    broadcastCountry,
    broadcastCompany,
  ].filter(Boolean).length;

  // [2026-02-10] Mobile: Filter content component (reused in sidebar and drawer)
  const filterContent = (
    <BroadcastFilters
      searchQuery={broadcastFilterKeyword}
      skuFilter={broadcastSKU}
      typeFilter={broadcastType}
      manufacturerFilter={broadcastManufacturer}
      categoryFilter={broadcastCategory}
      qtyFilter={broadcastQty}
      conditionFilter={broadcastCondition}
      countryFilter={broadcastCountry}
      companyFilter={broadcastCompany}
      onSearchChange={(value) => handleFilterChange("broadcastFilterKeyword", value)}
      onSkuChange={(value) => handleFilterChange("broadcastSKU", value)}
      onTypeChange={(value) => handleFilterChange("broadcastType", value)}
      onManufacturerChange={(value) => handleFilterChange("broadcastManufacturer", value)}
      onCategoryChange={(value) => handleFilterChange("broadcastCategory", value)}
      onQtyChange={(value) => handleFilterChange("broadcastQty", value)}
      onConditionChange={(value) => handleFilterChange("broadcastCondition", value)}
      onCountryChange={(value) => handleFilterChange("broadcastCountry", value)}
      onCompanyChange={(value) => handleFilterChange("broadcastCompany", value)}
      onClearFilters={handleClearFilters}
      broadcastItems={filteredBroadcasts}
      allBroadcastItems={allFlattenedBroadcasts}
      collapsed={false}
      onToggleCollapse={() => {}}
    />
  );

  useEffect(() => {
    if (loading || error) return;
    if (data.length === 0) {
      navigate("/broadcast/new", { replace: true });
    }
  }, [loading, error, data.length, navigate]);

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <SuccessMessage />
        <BroadcastsHeader title="My Broadcasts" createTo="/broadcast/edit" />
        <div className="py-4 text-center">Loading broadcasts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageContainer}>
        <SuccessMessage />
        <BroadcastsHeader title="My Broadcasts" createTo="/broadcast/edit" />
        <div className="py-4 text-center text-danger">{error}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return null;
  }

  return (
    <div className={styles.pageContainer}>
      <SuccessMessage />
      <BroadcastsHeader title="My Broadcasts" createTo="/broadcast/edit" />
      <div className={`container-fluid ${styles.fullWidthContainer}`}>
        {/* [2026-02-10] Mobile: Filter button and drawer */}
        {isMobile && (
          <>
            <div className="d-flex justify-content-end mb-3 px-3">
              <button
                className={`filter-button ${activeFilterCount > 0 ? "has-filters" : ""}`}
                onClick={() => setFilterDrawerOpen(true)}
              >
                <svg className="filter-button__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="filter-button__count">{activeFilterCount}</span>
                )}
              </button>
            </div>
            <FilterDrawer
              isOpen={filterDrawerOpen}
              onClose={() => setFilterDrawerOpen(false)}
              title="Filters"
              activeFilterCount={activeFilterCount}
              onClear={handleClearFilters}
              onApply={() => setFilterDrawerOpen(false)}
            >
              {filterContent}
            </FilterDrawer>
          </>
        )}

        <div className={`row g-0 ${styles.stretchRow}`}>
          {/* [2026-02-10] Mobile: Hide sidebar on mobile, show filter drawer instead */}
          {!isMobile && (
            <aside
              className={`${
                filtersCollapsed ? "col-1" : "col-12 col-md-4 col-lg-3"
              } ps-3 pe-3 d-flex flex-column ${filtersCollapsed ? styles.filtersSidebarCollapsed : styles.filtersSidebar}`}
            >
              <BroadcastFilters
                searchQuery={broadcastFilterKeyword}
                skuFilter={broadcastSKU}
                typeFilter={broadcastType}
                manufacturerFilter={broadcastManufacturer}
                categoryFilter={broadcastCategory}
                qtyFilter={broadcastQty}
                conditionFilter={broadcastCondition}
                countryFilter={broadcastCountry}
                companyFilter={broadcastCompany}
                onSearchChange={(value) =>
                  handleFilterChange("broadcastFilterKeyword", value)
                }
                onSkuChange={(value) => handleFilterChange("broadcastSKU", value)}
                onTypeChange={(value) =>
                  handleFilterChange("broadcastType", value)
                }
                onManufacturerChange={(value) =>
                  handleFilterChange("broadcastManufacturer", value)
                }
                onCategoryChange={(value) =>
                  handleFilterChange("broadcastCategory", value)
                }
                onQtyChange={(value) =>
                  handleFilterChange("broadcastQty", value)
                }
                onConditionChange={(value) =>
                  handleFilterChange("broadcastCondition", value)
                }
                onCountryChange={(value) =>
                  handleFilterChange("broadcastCountry", value)
                }
                onCompanyChange={(value) =>
                  handleFilterChange("broadcastCompany", value)
                }
                onClearFilters={handleClearFilters}
                broadcastItems={filteredBroadcasts}
                allBroadcastItems={allFlattenedBroadcasts}
                collapsed={filtersCollapsed}
                onToggleCollapse={toggleFilters}
              />
            </aside>
          )}

          {/* [2026-02-10] Mobile: Full width on mobile, otherwise use sidebar layout */}
          <div
            className={`${
              isMobile ? "col-12" : filtersCollapsed ? "col-11" : "col-12 col-md-8 col-lg-9"
            } bg-white border-bottom border-start-0 d-flex flex-column`}
          >
            <div className={`p-3 ${styles.contentArea}`}>
              <DashboardBroadcastSummaryTable
                broadcasts={reconstructed}
                currentTab="mine"
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                refreshCallback={refresh}
                showMobileFooter={true}
                showMobileActions={true}
              />
              {totalPages > 0 && (
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredBroadcasts.length}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  loading={loading}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastMinePage;
